import {
  extractPropertyKeys,
  isValidGeoJSONFile,
  transformToFeatures,
} from "./boundaryUtils";
import type { GeoJSONData, GeoJSONFeature, GeoJSONType } from "./types";
import { validateGeoJSON } from "./validateGeoJSON";

export interface FileUploadResult {
  isValid: boolean;
  geoJsonData: GeoJSONData | null;
  geoJsonType: GeoJSONType | null;
  features: GeoJSONFeature[];
  propertyKeys: string[];
  error: string | null;
}

/**
 * Handles GeoJSON file upload and processing
 */
export const handleGeoJSONFileUpload = async (
  file: File
): Promise<FileUploadResult> => {
  const result: FileUploadResult = {
    isValid: false,
    geoJsonData: null,
    geoJsonType: null,
    features: [],
    propertyKeys: [],
    error: null,
  };

  // Validate file extension
  if (!isValidGeoJSONFile(file.name)) {
    result.error = "Invalid file type. Please upload a .geoJSON file.";
    return result;
  }

  try {
    const content = await file.text();
    const validation = validateGeoJSON(content);

    if (!validation.isValid || !validation.data || !validation.type) {
      result.error = validation.error || "Invalid GeoJSON file";
      return result;
    }

    // Transform to normalized features
    const transformedFeatures = transformToFeatures(
      validation.data,
      validation.type
    );
    const propertyKeys = extractPropertyKeys(transformedFeatures);

    result.isValid = true;
    result.geoJsonData = validation.data;
    result.geoJsonType = validation.type;
    result.features = transformedFeatures;
    result.propertyKeys = propertyKeys;

    return result;
  } catch (error) {
    result.error = "Failed to read file";
    return result;
  }
};

/**
 * Common validation rules for boundary operations
 */
export const validateBoundaryOperation = {
  /**
   * Validates if import operation can proceed
   */
  canImport: (
    geoJsonData: GeoJSONData | null,
    geoJsonType: GeoJSONType | null,
    selectedNameKey: string,
    boundaryName: string,
    hasUploadError: boolean
  ): boolean => {
    if (!geoJsonData || hasUploadError) return false;

    if (geoJsonType === "FeatureCollection" && !selectedNameKey) return false;
    if (
      (geoJsonType === "Feature" || geoJsonType === "Geometry") &&
      !boundaryName.trim()
    )
      return false;

    return true;
  },

  /**
   * Validates if edit operation can proceed
   */
  canEdit: (
    boundaryKey: string,
    geoJsonType: GeoJSONType | null,
    selectedNameKey: string,
    features: GeoJSONFeature[],
    hasUploadError: boolean
  ): boolean => {
    if (!boundaryKey.trim() || hasUploadError) return false;

    // If user uploaded new geometry, validate it
    if (
      features.length > 0 &&
      geoJsonType === "FeatureCollection" &&
      !selectedNameKey
    )
      return false;

    return true;
  },
};

/**
 * Common error messages for boundary operations
 */
export const BoundaryErrorMessages = {
  INVALID_FILE_TYPE: "Invalid file type. Please upload a .geoJSON file.",
  INVALID_GEOJSON: "Invalid GeoJSON file",
  FAILED_TO_READ: "Failed to read file",
  MISSING_BOUNDARY_NAME: "Boundary name is required",
  MISSING_PROPERTY_SELECTION: "Please select a property for feature names",
  UPLOAD_FAILED: "Failed to upload file",
} as const;
